/**
 * Domain router for the AI Cortex multi-domain pipeline.
 *
 * Classifies incoming user messages as Discovery, Clinical, or
 * Corporate using keyword-based analysis.  Follows the same pattern
 * as {@link module:gibbs/text-to-action/routeRequest~isWorkflowRequest}.
 *
 * @module gibbs/cortex/domainRouter
 */

/**
 * Keywords associated with the Discovery domain.
 * @type {string[]}
 */
const DISCOVERY_KEYWORDS = [
    'protein', 'compound', 'dock', 'docking', 'minimize',
    'fragment', 'admet', 'energy', 'scaffold', 'ligand',
    'binding', 'pdb', 'smiles', 'kinase', 'selectivity',
    'boltzmann', 'forcefield',
];

/**
 * Keywords associated with the Clinical domain.
 * @type {string[]}
 */
const CLINICAL_KEYWORDS = [
    'trial', 'patient', 'dose', 'dosing', 'sad', 'mad',
    'efficacy', 'safety', 'adverse', 'biomarker', 'endpoint',
    'phase', 'cohort', 'pharmacokinetic', 'pk', 'pd', 'glp',
];

/**
 * Keywords associated with the Corporate domain.
 * @type {string[]}
 */
const CORPORATE_KEYWORDS = [
    'investor', 'revenue', 'earnings', 'press release',
    'competitive', 'market', 'pipeline', 'sec', 'filing',
    'shareholder', 'guidance', 'forecast',
];

/**
 * Count how many keywords from a list appear in the message.
 *
 * @param {string} lower - Lowercased message text
 * @param {string[]} keywords - Domain keyword list
 * @returns {number} Hit count
 */
function countHits(lower, keywords) {
    return keywords.reduce(
        (sum, kw) => sum + (lower.includes(kw) ? 1 : 0),
        0
    );
}

/**
 * Classify a user message into one or more domains.
 *
 * When the message contains keywords from two or more domains the
 * result includes both `primary` and `secondary` fields to support
 * cross-domain retrieval (decision D6).
 *
 * @param {string} message - Raw user input
 * @param {Object} [context] - Optional context
 * @param {string} [context.selectedDomain] - Manual domain override
 * @returns {{
 *   domain: string,
 *   primary?: string,
 *   secondary?: string,
 *   crossDomain: boolean,
 *   scores: Object<string, number>
 * }}
 */
export function classifyDomain(message, context = {}) {
    // Manual override takes precedence (verification: manual-domain-override)
    if (context.selectedDomain) {
        return {
            domain: context.selectedDomain,
            crossDomain: false,
            scores: {},
        };
    }

    const lower = message.toLowerCase();

    const scores = {
        discovery: countHits(lower, DISCOVERY_KEYWORDS),
        clinical: countHits(lower, CLINICAL_KEYWORDS),
        corporate: countHits(lower, CORPORATE_KEYWORDS),
    };

    // Sort domains by score descending
    const ranked = Object.entries(scores)
        .sort((a, b) => b[1] - a[1]);

    const [primary, primaryScore] = ranked[0];
    const [secondary, secondaryScore] = ranked[1];

    // Cross-domain: two or more domains have at least one hit
    if (primaryScore > 0 && secondaryScore > 0) {
        return {
            domain: primary,
            primary,
            secondary,
            crossDomain: true,
            scores,
        };
    }

    // Single domain or no signal (default to discovery)
    return {
        domain: primaryScore > 0 ? primary : 'discovery',
        crossDomain: false,
        scores,
    };
}
